import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";

// Claude: extracted from MeetingList.tsx 23-03-2026
interface Props {
  isDrawerOpen: boolean;
  onDrawerClose: () => void;
  selectedProfile: any;
  drawerNotes: string;
  drawerSellerEvents: any[];
}

const ProfileDrawer = ({
  isDrawerOpen,
  onDrawerClose,
  selectedProfile,
  drawerNotes,
  drawerSellerEvents,
}: Props) => {
  if (!selectedProfile) return null;

  // Profile is a seller if it has personName; buyer if it has buyerPersonName
  const isSeller = !!selectedProfile.personName;

  return (
    <Drawer isOpen={isDrawerOpen} placement="right" onClose={onDrawerClose} size="sm">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          {isSeller ? "Seller Profile" : "Buyer Profile"}
        </DrawerHeader>
        <DrawerBody>
          <VStack align="start" spacing={4} mt={2}>
            {/* Claude: LinkedIn photo avatar in drawer 09-02-2026 */}
            {selectedProfile.linkedinPhoto && (
              <Flex w="100%" justify="center">
                <Image
                  src={selectedProfile.linkedinPhoto}
                  alt="Profile"
                  boxSize="80px"
                  borderRadius="full"
                  objectFit="cover"
                  border="2px solid"
                  borderColor="gray.200"
                />
              </Flex>
            )}

            {isSeller ? (
              <>
                <Box>
                  <Text fontSize="xs" color="gray.500">Name</Text>
                  <Flex alignItems="center" gap={2}>
                    <Text fontWeight={600}>{selectedProfile.personName}</Text>
                    {/* Claude: LinkedIn verification status — always shown 17-02-2026 */}
                    {selectedProfile.linkedinVerified && (
                      <Badge colorScheme="green" fontSize="2xs" px={1.5} py={0.5} borderRadius="4px" textTransform="none">
                        LinkedIn Verified
                      </Badge>
                    )}
                  </Flex>
                </Box>
                {selectedProfile.personDesignation && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Designation</Text>
                    <Text>{selectedProfile.personDesignation}</Text>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xs" color="gray.500">Company</Text>
                  <Text fontWeight={600}>{selectedProfile.companyName}</Text>
                </Box>
                {(selectedProfile.companyHqCity || selectedProfile.companyHqState || selectedProfile.companyHqCountry) && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Company HQ</Text>
                    <Text>
                      {[selectedProfile.companyHqCity, selectedProfile.companyHqState, selectedProfile.companyHqCountry]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  </Box>
                )}
                {selectedProfile.companyTimezone && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Timezone</Text>
                    <Text>{selectedProfile.companyTimezone}</Text>
                  </Box>
                )}
                {/* Claude: Tradeshows section — above Tags 24-02-2026 */}
                {drawerSellerEvents.length > 0 && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Tradeshows</Text>
                    <Flex gap={1} flexWrap="wrap" mt={1}>
                      {drawerSellerEvents.map((ev: any, i: number) => (
                        <span
                          key={i}
                          style={{
                            padding: "2px 7px",
                            backgroundColor: "#EEF2FF",
                            color: "#3730A3",
                            borderRadius: "3px",
                            fontSize: "10px",
                          }}
                        >
                          {ev.eventName}
                        </span>
                      ))}
                    </Flex>
                  </Box>
                )}
                {selectedProfile.productTypeTags && selectedProfile.productTypeTags.length > 0 && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Tags</Text>
                    <Flex gap={2} flexWrap="wrap" mt={1}>
                      {selectedProfile.productTypeTags.slice(0, 2).map((tag: string, i: number) => (
                        <Text key={i} fontSize="sm">{tag}</Text>
                      ))}
                      {selectedProfile.productTypeTags.length > 2 && (
                        <Text fontSize="sm">+{selectedProfile.productTypeTags.length - 2} more</Text>
                      )}
                    </Flex>
                  </Box>
                )}
                {selectedProfile.companyUrl && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Website</Text>
                    <Link
                      href={selectedProfile.companyUrl.startsWith("http") ? selectedProfile.companyUrl : `https://${selectedProfile.companyUrl}`}
                      isExternal
                      color="blue.600"
                    >
                      {selectedProfile.companyUrl}
                    </Link>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box>
                  <Text fontSize="xs" color="gray.500">Name</Text>
                  <Flex alignItems="center" gap={2}>
                    <Text fontWeight={600}>{selectedProfile.buyerPersonName}</Text>
                    {/* Claude: LinkedIn verification status — always shown 17-02-2026 */}
                    {selectedProfile.linkedinVerified && (
                      <Badge colorScheme="green" fontSize="2xs" px={1.5} py={0.5} borderRadius="4px" textTransform="none">
                        LinkedIn Verified
                      </Badge>
                    )}
                  </Flex>
                </Box>
                {selectedProfile.buyerDesignation && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Designation</Text>
                    <Text>{selectedProfile.buyerDesignation}</Text>
                  </Box>
                )}
                {selectedProfile.buyerCompanyName && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Company</Text>
                    <Text fontWeight={600}>{selectedProfile.buyerCompanyName}</Text>
                  </Box>
                )}
                {selectedProfile.buyerCompanyInfo && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">About Company</Text>
                    <Text>{selectedProfile.buyerCompanyInfo}</Text>
                  </Box>
                )}
                {(selectedProfile.buyerPersonCity || selectedProfile.buyerPersonCountry) && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Location</Text>
                    <Text>
                      {[selectedProfile.buyerPersonCity, selectedProfile.buyerPersonCountry]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  </Box>
                )}
                {selectedProfile.buyerCompanyWebsite && (
                  <Box>
                    <Text fontSize="xs" color="gray.500">Website</Text>
                    <Link
                      href={selectedProfile.buyerCompanyWebsite.startsWith("http") ? selectedProfile.buyerCompanyWebsite : `https://${selectedProfile.buyerCompanyWebsite}`}
                      isExternal
                      color="blue.600"
                    >
                      {selectedProfile.buyerCompanyWebsite}
                    </Link>
                  </Box>
                )}
              </>
            )}

            {/* Claude: Notes section in profile drawer 17-02-2026 */}
            {drawerNotes && (
              <Box w="100%" mt={2}>
                <Text fontSize="xs" color="gray.500">Notes</Text>
                <Box mt={1} p={3} bg="gray.50" borderRadius="8px" border="1px solid" borderColor="gray.200">
                  <Text fontSize="sm" whiteSpace="pre-wrap">{drawerNotes}</Text>
                </Box>
              </Box>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default ProfileDrawer;
